import { useState } from "react";
import { find } from "lodash";
import { routerConfig } from "routes/router";
import { useAppSelector } from "store/hooks";
import { bankCardsSelector, companyMetadataSelector, userMetadataSelector } from "store/selectors";

import { UserRole } from "types/BETypes";
import { getConditionalClassName } from "helpers/shared/classNames";
import {
  getMainMenuCollapsedFromLocalStorage,
  setMainMenuCollapsedToLocalStorage,
} from "helpers/shared/mainMenu";

import {
  getAdminMainMenuOptions,
  getEmployeeMainMenuOptions,
  MAIN_MENU_ANIMATION_CLASS_NAME,
  MAIN_MENU_COLLAPSED_STATE_CLASS_NAME,
  MAIN_MENU_TRANSITION_DURATION,
} from "./constants";
import { IMainMenuItem } from "./types";

export const useMainMenu = () => {
  const currentUser = useAppSelector(userMetadataSelector);
  const currentCompany = useAppSelector(companyMetadataSelector);
  const currentBankCards = useAppSelector(bankCardsSelector);
  const lastActiveRole = currentUser?.activeCompanyUser?.lastActiveRole;
  const isMainMenuCollapsed = getMainMenuCollapsedFromLocalStorage();

  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isCollapsed, setCollapsed] = useState<boolean>(isMainMenuCollapsed);

  const animationClassName = getConditionalClassName(shouldAnimate, MAIN_MENU_ANIMATION_CLASS_NAME);
  const collapsedClassName = getConditionalClassName(
    isCollapsed,
    MAIN_MENU_COLLAPSED_STATE_CLASS_NAME.COLLAPSED,
    MAIN_MENU_COLLAPSED_STATE_CLASS_NAME.NOT_COLLAPSED,
  );
  const classNameByRole = `role-${lastActiveRole}`;

  const handleCollapse = () => {
    setMainMenuCollapsedToLocalStorage(!isCollapsed);
    setCollapsed((prev) => !prev);

    setShouldAnimate(true);
    setTimeout(() => {
      setShouldAnimate(false);
    }, MAIN_MENU_TRANSITION_DURATION);
  };

  const pageIsHidden = (option?: IMainMenuItem) => {
    if (!option) return false;

    const routeFromConfig = find(routerConfig, (item) => item.path === option.url);
    if (!routeFromConfig) return false;

    return (
      routeFromConfig?.disableCondition?.(currentUser, currentCompany, currentBankCards) ||
      !!option?.hide
    );
  };

  const getModifiedMenuOptions = (options: IMainMenuItem[] = []) => {
    return options
      .map((item) => ({
        ...item,
        hide: pageIsHidden(item),
      }))
      .filter((it) => !it.hide);
  };

  const getMenu = () => {
    const adminOptions = getAdminMainMenuOptions();
    const employeeOptions = getEmployeeMainMenuOptions();
    const options = {
      [UserRole.SUPERADMIN]: adminOptions,
      [UserRole.ADMIN]: adminOptions,
      [UserRole.OWNER]: adminOptions,
      [UserRole.EMPLOYEE]: employeeOptions,
    };

    return getModifiedMenuOptions(
      options[currentUser?.activeCompanyUser?.lastActiveRole as UserRole] || [],
    );
  };

  return {
    metadata: {
      getMenu,
      currentUser,
    },
    actions: {
      handleCollapse,
    },
    classNames: {
      animationClassName,
      collapsedClassName,
      classNameByRole,
    },
  };
};
